import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Card, Field, Input, Alert, SecretReveal } from '../components/ui';

/**
 * Password change form.
 *
 * Reused in two places: the account page, and the blocking screen a user sees
 * when they are still on an admin-issued password.
 *
 * Changing a password ends every session for that account, including this one,
 * which is the correct behaviour after a suspected compromise. The UI says so
 * before the user commits rather than dumping them at a login screen with no
 * explanation.
 */
export function ChangePasswordForm({
  forced = false,
  onDone,
}: {
  forced?: boolean;
  onDone?: () => void;
}) {
  const { logout } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit = current && next.length >= 12 && next === confirm && !busy;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await api.post('/auth/change-password', {
        currentPassword: current,
        newPassword: next,
      });

      setDone(true);

      // The server has already invalidated this session, so clear the client
      // state and send them to the login screen rather than leaving the app
      // in a half-authenticated state.
      setTimeout(() => {
        void logout().then(() => {
          onDone?.();
          window.location.href = '/login';
        });
      }, 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change your password.');
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Alert tone="good" title="Password changed">
        For security, every session has been signed out. Taking you to the sign-in screen…
      </Alert>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {error && <Alert tone="critical">{error}</Alert>}

      {forced && (
        <Alert tone="warning" title="Choose your own password">
          You are still using a password that was generated for you. Set one only you know before
          carrying on.
        </Alert>
      )}

      <Field label={forced ? 'Current (generated) password' : 'Current password'}>
        <Input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
          autoFocus
          required
        />
      </Field>

      <Field
        label="New password"
        hint="At least 12 characters. A memorable phrase beats a short jumble of symbols."
        error={tooShort ? 'Must be at least 12 characters.' : null}
      >
        <Input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
          required
        />
      </Field>

      <Field
        label="Confirm new password"
        error={mismatch ? 'The two passwords do not match.' : null}
      >
        <Input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          required
        />
      </Field>

      <p className="text-xs text-ink-muted">
        Changing your password signs out every device, including this one. You will need to sign
        in again with the new password.
      </p>

      <Button type="submit" variant="primary" loading={busy} disabled={!canSubmit} className="w-full">
        Change password
      </Button>
    </form>
  );
}

/** Blocking screen shown when a user is still on an admin-issued password. */
export function ForcedPasswordChange() {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-lg font-semibold">Set your password</h1>
          <p className="mt-1 text-sm text-ink-muted">One step before you can continue.</p>
        </div>
        <div className="rounded-lg bg-surface p-6 ring-1 ring-hairline">
          <ChangePasswordForm forced />
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Account page
// ===========================================================================

export function Account() {
  const { user, refresh } = useAuth();

  const [totp, setTotp] = useState<{ secret: string; uri: string; qrDataUrl?: string } | null>(
    null,
  );
  const [code, setCode] = useState('');
  const [totpError, setTotpError] = useState<string | null>(null);
  const [totpDone, setTotpDone] = useState(false);

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Account</h1>
        <p className="text-sm text-ink-muted">
          {user?.name} · {user?.email}
        </p>
      </div>

      <Card title="Change password">
        <div className="p-4">
          <ChangePasswordForm />
        </div>
      </Card>

      <Card title="Two-factor authentication">
        <div className="space-y-3 p-4">
          {totpDone ? (
            <Alert tone="good" title="Two-factor is on">
              You will be asked for a code from your authenticator app each time you sign in.
            </Alert>
          ) : totp ? (
            <>
              <Alert tone="info">
                Scan this with Google Authenticator, then enter the six-digit code it shows to
                confirm. Two-factor is not switched on until you do — that way a mistyped secret
                cannot lock you out.
              </Alert>

              {totp.qrDataUrl && (
                <div className="flex justify-center">
                  {/* White plate behind the code: the UI is dark, and phone
                      cameras read a dark-on-light QR far more reliably than an
                      inverted one. */}
                  <img
                    src={totp.qrDataUrl}
                    alt="QR code for two-factor setup"
                    width={220}
                    height={220}
                    className="rounded bg-white p-2"
                  />
                </div>
              )}

              <div>
                <div className="mb-1 text-xs text-ink-muted">
                  Or enter this key by hand, if the camera will not play ball
                </div>
                <SecretReveal value={totp.secret} label="two-factor secret" />
              </div>

              {totpError && <Alert tone="critical">{totpError}</Alert>}

              <Field label="Code from your app">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  className="text-center font-mono text-lg tracking-[0.3em]"
                />
              </Field>

              <Button
                variant="primary"
                disabled={code.length !== 6}
                onClick={async () => {
                  setTotpError(null);
                  try {
                    await api.post('/auth/totp/confirm', { code });
                    setTotpDone(true);
                    await refresh();
                  } catch (err) {
                    setTotpError(
                      err instanceof ApiError ? err.message : 'Could not confirm that code.',
                    );
                  }
                }}
              >
                Turn on two-factor
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-ink-secondary">
                Adds a code from your phone on top of your password. Worth doing on an account that
                can launch and terminate production instances.
              </p>
              <Button
                onClick={async () => {
                  try {
                    setTotp(
                      await api.post<{ secret: string; uri: string; qrDataUrl?: string }>(
                        '/auth/totp/setup',
                      ),
                    );
                  } catch (err) {
                    setTotpError(err instanceof ApiError ? err.message : 'Could not start setup.');
                  }
                }}
              >
                Set up two-factor
              </Button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
